import type { ReactNode } from 'react'
import type { Document } from '../../types'

/**
 * The Evidence lens rail (design #5630, Figure 3; contract row S2).
 *
 * The Evidence lens is the Claims lens read backwards: one document, and every
 * claim it supports. The rail's job is to say WHICH scope the reader is in —
 * "the Focus rail sits at Document, so the scope is stated, not implied". On a
 * page that already shows a dozen panels, an unstated scope is how a reader
 * ends up thinking a per-document extraction is the whole picture.
 *
 * WHERE THE REVERSE EDGE COMES FROM
 * ---------------------------------
 * The reverse edge — the claims this document supports — comes from ONE place:
 * getProjectClaims(projectId), over
 * GET /innovateflow/novelty/project/{project_id}/claims. Both are live; when
 * that query has not resolved, `claims` arrives undefined and the list renders
 * an honest empty state rather than a spinner that never ends.
 *
 * It would be easy, and wrong, to fill that gap from the per-document
 * novelty_report.claim_analysis this page already renders. Those are the claims
 * extracted FROM this document — exactly the one-document view the Evidence
 * lens exists to replace — and, unlike A4's route, they are not re-filtered per
 * reader, so a document a reader is not entitled to could surface through them.
 * An honest empty state is the correct answer until the real edge lands.
 */
export interface EvidenceClaim {
  claim: string
  supporting_document_ids: string[]
}

export interface EvidenceLensRailProps {
  document: Document
  /** The folders of the parent invention, so the Folder row can name one (Bug #5836). */
  folders?: { folder_id: string; name: string }[]
  /** The parent invention's name, for the figure's Invention row (Bug #5836). */
  inventionName?: string
  /** Re-run the extraction. Rendered only when the indexing actually failed. */
  onRetryIndexing?: () => void
  retrying?: boolean
  /** Page-owned extras that belong with the document's own facts (e.g. the
   * "is this the right kind of document?" confirmation). */
  children?: ReactNode
}

/**
 * The figure writes "✓ 2h ago" on the Analysed row, because how LONG ago the
 * analysis ran is what a reader is judging when they look at it. Once that
 * stops being the useful answer (a couple of days), the date is.
 */
function whenAnalysed(value?: string): string {
  if (!value) return 'Not recorded'
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return 'Not recorded'
  const mins = Math.floor((Date.now() - then) / 60000)
  if (mins < 0) return `✓ ${formatDate(value)}`
  if (mins < 2) return '✓ just now'
  if (mins < 60) return `✓ ${mins}m ago`
  if (mins < 48 * 60) return `✓ ${Math.floor(mins / 60)}h ago`
  return `✓ ${formatDate(value)}`
}

function formatDate(value?: string) {
  if (!value) return 'Not recorded'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
    // Round-2 gate: the document card on the same page pins Australia/Sydney
    // (DocumentDetail.tsx); without it a 22:26Z upload read as the day before.
    timeZone: 'Australia/Sydney',
  })
}

/**
 * Bug #5836. The rail printed the stored enum, so Figure 3's "Specification
 * (PDF)" read "patent_spec" — the machine's word for the type, shown to a
 * person. These are the same labels DocumentDetail's own type badge uses, kept
 * here rather than imported so the rail stays a leaf component with no page
 * dependency.
 */
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  patent_spec: 'Patent Specification',
  financial: 'Financial Document',
  research_paper: 'Research Paper',
  presentation: 'Presentation',
  '3d_model': '3D Model',
  cad_drawing: 'CAD Drawing',
  technical_document: 'Technical Document',
  tabular_data: 'Tabular Data',
  image: 'Image',
  grant_application: 'Grant Application',
}

/** The file format in the parenthesis Figure 3 shows — from the real record only. */
function fileFormat(document: Document): string | null {
  const fromName = (document.filename || '').split('.').pop()
  if (fromName && fromName !== document.filename && fromName.length <= 5) {
    return fromName.toUpperCase()
  }
  const ct = document.content_type
  if (!ct) return null
  const sub = ct.split('/').pop()
  if (!sub) return null
  if (sub === 'pdf') return 'PDF'
  if (sub === 'plain') return 'TXT'
  return sub.replace(/[^a-z0-9]/gi, '').slice(0, 5).toUpperCase() || null
}

function typeLabel(document: Document): string {
  const base = document.document_type
    ? DOCUMENT_TYPE_LABELS[document.document_type] ?? document.document_type
    : null
  const fmt = fileFormat(document)
  if (base && fmt) return `${base} (${fmt})`
  if (base) return base
  if (fmt) return fmt
  return 'Not recorded'
}

/** Bytes as a person reads them. Never rounds an unknown size into a number. */
function formatBytes(bytes?: number): string | null {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) return null
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i += 1
  }
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`
}

/**
 * Figure 3's "Size · pages" row, and the honest answer when the estate does not
 * hold those numbers.
 *
 * Measured against the real dev documents table on 5 Sep 2026: `file_size` is
 * set on 2 of 61 rows and NO page count is stored under any key — the schema
 * has no page field at all. So this row says what is actually known and states
 * the rest as not recorded, rather than printing a plausible number that came
 * from nowhere. When the ingest pipeline starts recording pages, this row picks
 * it up without another change here.
 */
function sizeAndPages(document: Document): string {
  const size = formatBytes(document.file_size)
  const rawPages = (document as unknown as { page_count?: number }).page_count
  const pages = typeof rawPages === 'number' && rawPages > 0 ? `${rawPages} pages` : null
  if (size && pages) return `${size} · ${pages}`
  if (size) return `${size} · pages not recorded`
  if (pages) return `Size not recorded · ${pages}`
  return 'Not recorded'
}

/**
 * What an indexing failure means, in the reader's words. Lifted verbatim from
 * the document page's old metadata grid when that grid was removed with the
 * rest of the shell the figure does not show: the diagnosis is the useful part,
 * and it would have been lost with the box it happened to live in.
 */
function indexingHelp(error?: string): string {
  const e = error ?? ''
  if (e.includes('Scanned PDF')) {
    return 'This PDF appears to be a scanned document. OCR extraction was attempted but could not read the content. Try uploading a text-searchable version.'
  }
  if (e.includes('Corrupt PDF')) {
    return 'This PDF appears to be damaged or incomplete. Try re-exporting from the original application and uploading again.'
  }
  if (e.includes('Protected PDF')) {
    return 'This PDF is password-protected or DRM-encrypted. Try uploading an unlocked version.'
  }
  if (e.includes('No text extracted')) {
    return 'No readable text could be extracted from this file. This is expected for scanned images, photos, or 3D-model files. If this is a text document, click “Retry” — the document is still stored safely and can be downloaded.'
  }
  if (e.includes('AuthenticationException') || e.includes('TransportError')) {
    return 'The analysis service was temporarily unavailable when this document was processed. Click “Retry” below to try again — this usually resolves itself.'
  }
  if (e.includes('timed out') || e.includes('Task timed out')) {
    return 'Processing took longer than expected, possibly due to file size. Click “Retry” to try again.'
  }
  return 'Processing encountered an issue. Click “Retry” below to try again — this is usually temporary.'
}

export default function EvidenceLensRail({
  document,
  folders,
  inventionName,
  onRetryIndexing,
  retrying,
  children,
}: EvidenceLensRailProps) {
  // The reverse edge: the claims whose support includes THIS document. This
  // filter IS the Evidence lens — everything else on the page is about the
  // document alone. The count of claims EXTRACTED from the file is a different
  // number and is labelled as such.
  const extracted = document.key_claims?.length ?? 0

  // Bug #5836. 'root' is the id the estate uses for a document that sits
  // directly in the invention, so it is the project root and not "a folder" —
  // the old placeholder called every document filed, including those that are
  // not. A folder id we cannot resolve is stated as such, never guessed.
  const folderId = document.folder_id
  const folderName = (() => {
    if (!folderId || folderId === 'root') return 'Project root'
    const match = (folders ?? []).find((f) => f.folder_id === folderId)
    if (match?.name) return match.name
    return folders ? 'Folder name unavailable' : 'In a folder'
  })()

  const analysed = document.embedding_status === 'completed'
  const facts: [string, string][] = [
    ['Type', typeLabel(document)],
    ['Size · pages', sizeAndPages(document)],
    ['Folder', folderName],
    ['Uploaded', formatDate(document.created_at)],
    ['Analysed', analysed ? whenAnalysed(document.updated_at) : 'Not yet analysed'],
    ['Claims extracted', String(extracted)],
    ['Invention', inventionName || 'Not recorded'],
  ]

  return (
    <section data-part="EvidenceLensRail" className="light-card p-4">
      <h3 className="mb-2.5 flex items-center gap-1.5 text-[13px] font-bold text-iflow-900">
        <span aria-hidden>📄</span> <span>Document</span>
      </h3>
      <dl>
        {facts.map(([label, value]) => (
          <div key={label} className="mb-2 flex items-baseline justify-between gap-3 text-[12.5px] last:mb-0">
            <dt className="shrink-0 text-gray-500">{label}</dt>
            <dd className={`text-right font-semibold ${label === 'Invention' ? 'text-iflow-700' : 'text-gray-700'}`}>{value}</dd>
          </div>
        ))}
      </dl>

      {document.tags && document.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 border-t border-sky-100 pt-3">
          {document.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-sky-500/10 px-2.5 py-0.5 text-[11px] font-medium text-gray-700">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* The indexing failure, what it means in plain words, and its retry live
          here — on the document's own facts — rather than in the page-wide
          banner the figure does not show. It appears only when the extraction
          actually failed, and it says WHICH failure, because "try again" is
          useless advice for a scanned image and correct advice for a timeout. */}
      {document.embedding_status === 'failed' && onRetryIndexing && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-[11.5px] leading-snug text-amber-800">{indexingHelp(document.embedding_error)}</p>
          <button
            type="button"
            onClick={onRetryIndexing}
            disabled={retrying}
            className="mt-1.5 rounded-md bg-iflow-700 px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-iflow-800 disabled:opacity-50"
          >
            {retrying ? 'Retrying…' : 'Retry Extraction'}
          </button>
        </div>
      )}

      {children}
    </section>
  )
}
